import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import AreaOptions from '../components/AreaOptions';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';
import './Community.css';

function statusAttr(status) {
  if (status === 'Closed') return 'Closed';
  if (status === 'Open') return 'Resolved';
  return 'New';
}

function PostCard({ post, busy, onInterest, onUpdate }) {
  return (
    <article className="help-card">
      <span className="help-status" data-status={statusAttr(post.status)}>
        {post.status}
      </span>
      <h2 className="help-card-title">{post.title}</h2>
      <p className="help-meta">
        {post.type} · {post.area}
        {post.createdByName ? ` · Posted by ${post.createdByName}` : ''}
      </p>
      <p className="help-body">{post.description}</p>
      <p className="help-hint">Contact goes through the Area Head. The phone number is not on this card.</p>
      {post.myInterest ? (
        <p className="help-meta">You sent interest. The poster and Area Head can see your name, skills, and phone.</p>
      ) : null}
      {post.status === 'Open' && !post.isPoster && !post.myInterest ? (
        <button type="button" className="help-btn primary" disabled={busy} onClick={() => onInterest(post._id)}>
          I’m interested
        </button>
      ) : null}
      {post.canManage && post.status === 'Pending approval' ? (
        <button
          type="button"
          className="help-btn primary"
          disabled={busy}
          onClick={() => onUpdate(post._id, { status: 'Open' })}
        >
          Approve
        </button>
      ) : null}
      {(post.isPoster || post.canManage) && post.status !== 'Closed' ? (
        <button type="button" className="help-btn" disabled={busy} onClick={() => onUpdate(post._id, { status: 'Closed' })}>
          Close
        </button>
      ) : null}
      {post.interests?.length ? (
        <ul className="help-list">
          {post.interests.map((interest) => (
            <li key={interest._id} className="help-card">
              <strong>{interest.memberName || 'Member'}</strong>
              <p className="help-meta">{interest.memberId || 'No member ID'}</p>
              <p className="help-meta">
                Skills: {interest.skills?.length ? interest.skills.join(', ') : 'None listed'}
              </p>
              <p className="help-meta">Phone: {interest.phone || 'No phone on profile'}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

export default function Jobs() {
  const [posts, setPosts] = useState([]);
  const [area, setArea] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Job');
  const [postArea, setPostArea] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const role = sessionStorage.getItem('sangramUserRole');
  const approved = getSessionStatus() === 'Active';

  const load = useCallback(async () => {
    setError('');
    const res = await apiFetch(apiUrl(`/jobs?${pagingParams(page, pageSize)}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load jobs');
    setArea(data.area || '');
    setPostArea((current) => current || data.area || '');
    setPosts(data.posts || []);
    setTotal(data.total ?? (data.posts || []).length);
  }, [page, pageSize]);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load jobs'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const createPost = async (e) => {
    e.preventDefault();
    setBusyKey('create');
    setError('');
    setNotice('');
    try {
      const body = { title, description, type };
      if (role === 'Admin') body.area = postArea;
      const res = await apiFetch(apiUrl('/jobs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not post work');
        return;
      }
      setTitle('');
      setDescription('');
      setType('Job');
      setNotice(
        data.post?.status === 'Pending approval'
          ? 'Posted. It stays hidden until your Area Head approves it.'
          : 'Posted for your area.'
      );
      setPage(1);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const updatePost = async (id, body) => {
    setBusyKey(id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/jobs/${id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update work');
        return;
      }
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const sendInterest = async (id) => {
    setBusyKey(id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/jobs/${id}/interest`), { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send interest');
        return;
      }
      setNotice('Interest sent. The poster and Area Head can see your phone.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">Jobs</h1>
            <p className="help-sub">
              {area
                ? `Work posted in ${area}. A post from another area stays hidden.`
                : 'Work posted in your area.'}
            </p>
          </div>
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            Jobs are available after your membership is approved.
          </p>
        ) : null}
        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="help-notice">{notice}</p> : null}

        {approved ? (
          <form className="help-form" onSubmit={createPost}>
            <h2 className="help-card-title">Post work</h2>
            <label className="help-field">
              Title
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
            </label>
            <label className="help-field">
              Type
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="Job">Job</option>
                <option value="Short work">Short work</option>
              </select>
            </label>
            <label className="help-field">
              What is the work?
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} required />
            </label>
            {role === 'Admin' ? (
              <label className="help-field">
                Area
                <select value={postArea} onChange={(e) => setPostArea(e.target.value)} required>
                  <AreaOptions current={postArea} />
                </select>
              </label>
            ) : (
              <p className="help-hint">
                {role === 'Area Head'
                  ? `This work goes live in ${area || 'your area'}.`
                  : `This work is posted for ${area || 'your area'} and stays hidden until the Area Head approves it.`}
              </p>
            )}
            <button type="submit" className="help-btn primary" disabled={Boolean(busyKey)}>
              {busyKey === 'create' ? 'Saving…' : 'Post work'}
            </button>
          </form>
        ) : null}

        {loading ? <p className="help-muted">Loading…</p> : null}
        {approved && !loading && posts.length === 0 ? (
          <p className="help-muted">No work in your area yet.</p>
        ) : null}
        <ul className="help-list">
          {posts.map((post) => (
            <li key={post._id}>
              <PostCard post={post} busy={Boolean(busyKey)} onInterest={sendInterest} onUpdate={updatePost} />
            </li>
          ))}
        </ul>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
